import { Card, H5, H6, InputIcon } from "@sellar-io/components";
import {
  RetailerListElementResponseType,
  RetailerListParameterModifierType,
} from "@sellar-io/api-v2/types";
import { pluralize } from "@sellar-io/common";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useDebounce } from "use-debounce";
import { ListElement, SpinnerLegacy } from "../../../components";
import { Badge, BadgeGreen } from "../../../components/badge/Badge";
import { useDashboardContext } from "../../../components/dashboard/Dashboard.shared";
import { InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";
import {
  Listbox,
  ListboxInputButton,
  ListboxOptions,
  ListboxOption,
  ListboxHeader,
} from "../../../components/listbox/Listbox";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { StatePrompts } from "../../../components/statePrompts/statePrompts";
import { CUSTOMERS, BUYER } from "../../../constants/routes";
import { useBuyerList } from "../../../hooks/api/v2/retailer/useBuyerList";
import { useParamAsNumber } from "../../../hooks/useParamAsNumber";
import { useSearchParamState } from "../../../hooks/useSearchParamState";
import { FilterType } from "../../../lists/types";

import { DiscoveryFilterButton } from "../../discovery/v2/components/DiscoveryFilterButton";
import { CreateBuyerDialog } from "./Dialogs/CreateBuyerDialog";

type CustomerListSearchType = "name" | "postalCode";

const CustomerListSearchValues: FilterType<
  RetailerListParameterModifierType,
  CustomerListSearchType
> = {
  name: {
    key: "name",
    name: "Name",
    mutation: (value: string) => [
      {
        type: "name",
        modifier: "INCLUDES",
        value,
      },
    ],
  },
  postalCode: {
    key: "postalCode",
    name: "Postcode",
    mutation: (value: string) => [
      {
        type: "postalCode",
        modifier: "INCLUDES",
        value,
      },
    ],
  },
};

export const BuyerSearchList = () => {
  const navigate = useNavigate();

  const [searchType, setSearchType] =
    useSearchParamState<CustomerListSearchType>("searchType", "name");

  const [searchValue, setSearchValue] = useSearchParamState("search");
  const [selectedPostcode, setSelectedPostcode] = useState<string>();
  const [debouncedSearchValue] = useDebounce(searchValue, 500);

  const [newBuyerDialogActive, setNewBuyerDialogActive] =
    useState<boolean>(false);
  const { isTwoPanel } = useDashboardContext();

  const buyerListQuery = useBuyerList(
    {
      limit: 10,
      query: [
        {
          type: searchType,
          modifier: "INCLUDES",
          value: debouncedSearchValue,
        },
        ...(selectedPostcode && searchType === "name"
          ? [
              {
                type: "postalCode" as const,
                modifier: "INCLUDES" as const,
                value: selectedPostcode,
              },
            ]
          : []),
      ],
    },
    {
      enabled: debouncedSearchValue.length > 1,
    },
  );

  return (
    <>
      <NavigationBar
        title={"Add Customer"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => navigate(CUSTOMERS)}
      />
      <div
        className={twMerge(
          !isTwoPanel && "m-auto flex max-w-[800px] flex-col gap-4 p-4",
        )}
      >
        <Card
          className={twMerge(
            "bg-white",
            "sticky top-[var(--nav-height)] flex p-5",
          )}
        >
          <InputContainer className={"w-full"}>
            <InputIcon icon={"MAGNIFY"} />
            <InputText
              placeholder={"Search..."}
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setSelectedPostcode(undefined);
              }}
            />
            <Listbox
              value={searchType}
              onChange={(value) => {
                setSearchType(value as CustomerListSearchType);
                setSelectedPostcode(undefined);
              }}
            >
              <ListboxInputButton>
                {CustomerListSearchValues[searchType].name}
              </ListboxInputButton>
              <ListboxOptions anchor={"bottom end"}>
                <ListboxHeader>Search by</ListboxHeader>
                {Object.values(CustomerListSearchValues).map((value, i) => (
                  <ListboxOption key={i} value={value.key}>
                    {value.name}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Listbox>
          </InputContainer>
        </Card>
        {debouncedSearchValue.length < 2 ? (
          <Card
            className={twMerge(
              "bg-white",
              "flex flex-col items-center gap-2 px-5 py-10 text-center",
            )}
          >
            <H5>Set your customers up with terms and pricing</H5>
            <span>
              Search for your customers by name or postcode to see if they're
              already active on Sellar. If not, you can always add their details
              manually.
            </span>
          </Card>
        ) : buyerListQuery.isLoading ? (
          <SpinnerLegacy />
        ) : (
          <>
            {buyerListQuery.data?.pages[0]?.length ? (
              <BuyerListAndFilter
                buyerListQuery={buyerListQuery}
                searchType={searchType}
                selectedPostcode={selectedPostcode}
                setSelectedPostcode={setSelectedPostcode}
              />
            ) : null}
            {!buyerListQuery.hasNextPage && (
              <StatePrompts
                title={"Don't see who you're looking for?"}
                description={
                  "Add their details manually and set them up with terms and pricing"
                }
                onClick={() => setNewBuyerDialogActive(true)}
                primaryCta={"Add customer"}
                icon={"PLUS"}
              />
            )}
            <CreateBuyerDialog
              active={newBuyerDialogActive}
              onDismiss={() => setNewBuyerDialogActive(false)}
            />
          </>
        )}
      </div>
    </>
  );
};

const BuyerListAndFilter = ({
  searchType,
  buyerListQuery,
  setSelectedPostcode,
  selectedPostcode,
}: {
  searchType: CustomerListSearchType;
  buyerListQuery: UseInfiniteQueryResult<
    InfiniteData<RetailerListElementResponseType[]>
  >;
  selectedPostcode: string | undefined;
  setSelectedPostcode: (selectedPostcode: string | undefined) => void;
}) => {
  const navigate = useNavigate();
  const buyerId = useParamAsNumber(BUYER, "buyerId", false);
  const { isTwoPanel } = useDashboardContext();
  const location = useLocation();
  const isNameFilter = searchType === "name";
  const buyerPostcodes = isNameFilter
    ? [
        ...new Set(
          buyerListQuery.data?.pages.flatMap((page) =>
            page.map(
              (item) => item.Business.DeliveryAddress.postalCode.split(" ")[0],
            ),
          ),
        ),
      ]
    : null;
  const isBuyerSelected = !!buyerId;
  return (
    <>
      {buyerPostcodes?.length && (
        <div
          className={twMerge("flex flex-col gap-2", isBuyerSelected && "p-2")}
        >
          <H5>FILTER BY POSTCODE</H5>
          <div className="flex flex-wrap gap-2">
            {buyerPostcodes.map((postcode) => (
              <DiscoveryFilterButton
                onClick={() => {
                  setSelectedPostcode(
                    postcode === selectedPostcode ? undefined : postcode,
                  );
                }}
                selected={selectedPostcode === postcode}
                key={postcode}
                title={postcode ?? ""}
              />
            ))}
          </div>
        </div>
      )}
      <InfiniteList
        query={buyerListQuery}
        className={"overflow-hidden rounded-lg"}
      >
        {buyerListQuery.data?.pages.map((page) =>
          page.map((buyer) => (
            <>
              <ListElement
                selected={isTwoPanel && buyer.id === buyerId}
                promptText={"See details"}
                key={buyer.id}
                onClick={() =>
                  navigate(BUYER.href({ buyerId: buyer.id }) + location.search)
                }
              >
                <div className={"flex flex-col gap-2"}>
                  <div
                    className={twMerge(
                      "flex flex-wrap items-center gap-2",
                      isBuyerSelected && "flex-col items-start",
                    )}
                  >
                    <H5>{buyer.Business.name}</H5>
                    {!!buyer.Business.registeredCompanyName && (
                      <H6>({buyer.Business.registeredCompanyName})</H6>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {!!buyer.Business.BusinessUsers.length ? (
                        <BadgeGreen icon={"ACCOUNT_MULTIPLE"}>
                          {buyer.Business.BusinessUsers.length} active users
                        </BadgeGreen>
                      ) : (
                        <Badge icon={"ACCOUNT_MULTIPLE"}>0 active users</Badge>
                      )}
                      {!!buyer.SupplierRetailerConnectionCount ? (
                        <BadgeGreen icon={"HANDSHAKE"}>
                          {buyer.SupplierRetailerConnectionCount >= 30
                            ? "30+"
                            : buyer.SupplierRetailerConnectionCount}{" "}
                          {pluralize(
                            "connection",
                            buyer.SupplierRetailerConnectionCount,
                          )}
                        </BadgeGreen>
                      ) : (
                        <Badge icon={"HANDSHAKE"}>0 connections</Badge>
                      )}
                    </div>
                  </div>
                  <div className={"flex flex-row gap-4"}>
                    <div className="flex flex-col">
                      <H6>Postcode</H6>
                      <H6>Last order</H6>
                    </div>
                    <div className="flex flex-col">
                      <H5>{buyer.Business.DeliveryAddress.postalCode}</H5>
                      {buyer.lastOrderedAt ? (
                        <>
                          {dayjs().diff(buyer.lastOrderedAt, "day") > 30 ? (
                            <p>
                              {dayjs(buyer.lastOrderedAt).format("DD/MM/YYYY")}{" "}
                              ({dayjs(buyer.lastOrderedAt).fromNow()})
                            </p>
                          ) : (
                            <H5>
                              {dayjs(buyer.lastOrderedAt).format("DD/MM/YYYY")}{" "}
                              ({dayjs(buyer.lastOrderedAt).fromNow()})
                            </H5>
                          )}
                        </>
                      ) : (
                        <p>No orders</p>
                      )}
                    </div>
                  </div>
                </div>
              </ListElement>
            </>
          )),
        )}
      </InfiniteList>
    </>
  );
};
